// The field vocabulary of a food-log entry, shared by the storage shape
// (./data), the public shape (./public), the api's model layer and the app.
import { properties } from "@repoflow/common/schema-fields";

const { createdAt, updatedAt } = properties;
export { createdAt, updatedAt };

export const orgId = { type: "string" };

// THE DAY IS THE FRONT OF THE KEY, AND THAT IS WHAT MAKES "TODAY" ONE QUERY.
//
// `entryId` is `<date>-<suffix>` — `2026-10-23-k3x9qa` — minted by the server at
// create. The range key sorts as a string, and an ISO date sorts as a string in
// day order, so every entry of one day is a contiguous run of the partition and
// `begins_with(entryId, "2026-10-23-")` reads exactly that day and nothing else.
// A week is a BETWEEN over the same key. Neither needs an index.
//
// The cost is that an entry cannot move to another day: the day is in the key,
// and a key is not updatable. `date` is therefore absent from the update shape,
// and "I logged that on the wrong day" is a remove and a re-add.
export const ENTRY_ID_RE = /^\d{4}-\d{2}-\d{2}-[a-z0-9]{6,16}$/;

export const entryId = { type: "string", pattern: ENTRY_ID_RE.source };

// The person's own calendar day, as `YYYY-MM-DD`, chosen by the CLIENT.
//
// Not derived from `createdAt` on the server, deliberately: the server runs in
// UTC and a dinner logged at 20:00 in Mexico City is already tomorrow there.
// Only the browser knows which day the person means.
export const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export const date = {
  label: "Date",
  type: "string",
  pattern: DATE_RE.source,
  // `format: "date"` is what makes form-schema's date widget store a calendar
  // day rather than a UTC instant — see its widgets/date.
  format: "date",
  widget: "date",
};

// The storage twin of `date`, and it is not optional.
//
// datorm reads `format: "date"` as a TIMESTAMP: Entity.serialize writes such a
// property as `toISOString()` and deserialize hands back a Date. The public
// shape needs the format — it is what makes form-schema's widget store a plain
// day — and the storage shape must not have it, or `2026-10-23` is written as
// `2026-10-23T00:00:00.000Z` and every comparison against the key's day prefix
// stops matching. Same field, two declarations, for the reason tablews'
// storedColumns has two.
export const storedDate = { type: "string", pattern: DATE_RE.source };

export const MEALS = ["breakfast", "lunch", "dinner", "snack"];

export const meal = {
  label: "Meal",
  type: "string",
  enum: MEALS,
  // What form-schema's select shows for each value, in the enum's order.
  enumLabels: [
    { label: "Breakfast" },
    { label: "Lunch" },
    { label: "Dinner" },
    { label: "Snack" },
  ],
  default: "snack",
};

export const food = { label: "Food", type: "string", minLength: 1, maxLength: 200 };

// Free text — "1 cup", "200 g", "2 slices". A quantity and a unit would invite
// arithmetic this service does not do: the numbers below are what the person
// says this serving holds, not a per-100 g figure scaled by a quantity.
export const serving = { label: "Serving", type: "string", maxLength: 100, default: "" };

// Energy in kcal, and the three macros in grams. Numbers rather than integers:
// a label says 2.5 g of fat often enough. Bounded above so a typo of 25000 is
// refused rather than summed into a day.
export const calories = { label: "Calories (kcal)", type: "number", minimum: 0, maximum: 10000, default: 0 };
export const protein = { label: "Protein (g)", type: "number", minimum: 0, maximum: 1000, default: 0 };
export const carbs = { label: "Carbs (g)", type: "number", minimum: 0, maximum: 1000, default: 0 };
export const fat = { label: "Fat (g)", type: "number", minimum: 0, maximum: 1000, default: 0 };

// The nutrient fields a day's total sums, in the order the screen shows them.
export const NUTRIENTS = ["calories", "protein", "carbs", "fat"];
